import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { isRaccoonError, useAsyncData } from '@myhealth-raccoon/services';
import moment from 'moment';

import { useDispatch, useSelector } from 'src/store';
import { isRequestError } from 'src/utils/requestError';
import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { bookingService } from 'src/services/booking';
import { ContentContainer } from 'src/components/ContentContainer';
import { PleaseWait } from 'src/components/PleaseWait';
import { useAppointmentTypeSelector, useClinicSelector, useDoctorSelector } from 'src/hooks/useAsyncSelector';
import { isFaceToFace } from 'src/utils/appointmentTypes';
import { stripHtml } from 'src/utils/html';
import { clinicName, doctorName } from 'src/utils/name';
import { patientService } from 'src/services/patient';
import { useBeforeUnload } from 'src/hooks/useBeforeUnload';

type Props = { nextPage: string }

export function ConfirmBookingPassThrough({ nextPage }: Props) {
    useBookingFlowStep(3);
    const navigate = useNavigate();
    // show dialog before closing/reloading
    useBeforeUnload();
    const dispatch = useDispatch();

    const site_id = useSelector(state => state.appointment.clinicId);
    const apptDateTime = useSelector(state => state.appointment.dateTime);
    const payment_id = useSelector(state => state.appointment.payment_id);
    const { appointment_id } = useSelector(state => state.appointment.reservedBooking!);
    const codeRequestType = useSelector(state => state.bookingFlow.codeRequestType);
    const { data: doctor, error: doctorError } = useDoctorSelector();
    const { data: appointmentType, error: apptTypeError } = useAppointmentTypeSelector();
    const { data: clinic, error: clinicError } = useClinicSelector();

    const { data: basicDetails, error: detailsError } = useAsyncData({
        method: patientService.GetBasicDetails,
        service: patientService
    }, site_id)

    useEffect(() => {
        if (!doctor || !appointmentType || !clinic || !basicDetails) {
            return;
        }
        const dateTime = moment(apptDateTime);
        (async () => {
            try {
                await bookingService.ConfirmBooking(site_id, appointment_id, {
                    sendSMS: codeRequestType !== "Email",
                    doctorProfileUrl: doctor.automedProfileUrl,
                    appointmentDuration: appointmentType.standardDurationInMins,
                    appointmentFaceToFace: isFaceToFace(appointmentType) ? "Face to Face" : "Telehealth",
                    clinicAddress: stripHtml(clinic.addressHtml),
                    clinicAddressUrl: `https://maps.google.com/?q=${stripHtml(clinic.addressHtml)}`,
                    clinicName: clinicName(clinic)!,
                    clinicTel: clinic.phoneNumber,
                    patientFullName: `${basicDetails.firstname} ${basicDetails.surname}`,
                    appointmentDate: dateTime.format("yyyy-MM-dd"),
                    appointmentTime: dateTime.format("LT"),
                    appointmentTypeName: appointmentType.appointmentName,
                    doctorFullName: doctorName(doctor)!,
                    paymentId: payment_id ? payment_id.toString() : undefined
                });
                navigate(nextPage, { replace: true });
            } catch (e) {
                if (e instanceof Error) {
                    // IF return reason = 4, then Booking was created, but something failed in sending confirmation. 
                    // IF return reason = 3 - the slot time has expired.
                    if (isRequestError(e) && isRaccoonError(e.data)) {
                        if (e.data.reason === 4) {
                            navigate(nextPage, { replace: true, state: { confirmNotifyFailed: true } });
                            return;
                        }
                        else if (e.data.reason === 3) {
                            navigate('/app/err-reserved-slot-expired', { replace: true });
                            return;
                        }
                    }
                    captureException(e)
                    navigate('/app/err-unexpected-error');
                }
            }
        })()
    }, [appointment_id, codeRequestType, navigate, site_id, nextPage, dispatch, doctor, appointmentType, clinic, basicDetails, apptDateTime, payment_id])

    return <ContentContainer>
        <PleaseWait error={detailsError || clinicError || apptTypeError || doctorError} />
    </ContentContainer>
}